import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config from '../../../constants/apiConfig';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useActualIncidentId } from './commonHooks';

export type Incident205aContact = {
  id: string;
  contact_id: string;
  incident_id: string;
  user_guid: string;
  version: number;
  assignment: string | null;
  name: string | null;
  cell_phone: string | null;
  radio_frequency: string | null;
  other: string | null;
  sort_order: number;
  timestamp: Date | string;
  archived: boolean;
};

export type CreateIncident205aContactReqData = {
  contact_id?: string;
  assignment?: string | null;
  name?: string | null;
  cell_phone?: string | null;
  radio_frequency?: string | null;
  other?: string | null;
  sort_order?: number;
  archived?: boolean;
};

export type UpdateIncident205aContactReqData =
  CreateIncident205aContactReqData & {
    contact_id: string;
  };

export const keys = {
  base: 'incident_205a_contacts',
  list: (incidentId: string, userId: string) =>
    [keys.base, incidentId, userId] as const,
} as const;

export const useIncident205aContacts = (scope: IapScope = INCIDENT) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<Incident205aContact[]>(
        `${config.apiGateway.ics_incident_205a_contacts}/${incidentId}`
      );
      return resp.data;
    },
    enabled: scope === INCIDENT && !!incidentId,
  });
};

export const useCreateIncident205aContact = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateIncident205aContactReqData) => {
      const resp = await axios.post<Incident205aContact>(
        `${config.apiGateway.ics_incident_205a_contacts}/${incidentId}`,
        { ...data, user_guid }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateIncident205aContact = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateIncident205aContactReqData) => {
      const resp = await axios.put<Incident205aContact>(
        `${config.apiGateway.ics_incident_205a_contacts}/${incidentId}/${data.contact_id}`,
        { ...data, user_guid }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
